import { memo, RefObject, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import {
  StaticDatePicker,
  PickersDayProps,
  PickersDay,
} from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTime } from 'infras/datetime';
import Skeleton from '../../../../../../../atoms/Skeleton';

import { CustomLuxonUtils } from 'infras/datetime/picker';

import { convertHexToRGBA } from 'presentations/views/helpers/color';
import { black, gray01, white } from 'presentations/views/styles/color';
import { fontSize, fontWeight, mediaMax } from 'presentations/views/styles';
import TextField from '@mui/material/TextField';

interface Props {
  datePickerRef?: RefObject<any>;
  locale?: string;
  className?: string;
  minDate?: Date;
  isSetMinDateUndefined?: boolean;
  maxDate?: Date;
  isLoading?: boolean;
  isDisabled?: boolean;
  isAvailable?: boolean;
  disablePast?: boolean;
  disableDates?: Date[];
  value?: DateTime | null;
  onSelect: (day: DateTime) => void;
  onChangeMonth?: any;
  shouldDisableDate?: any;
  defaultDate?: DateTime | null;
  startWeekDay?: number | null;
  color?: string;
  isLightColor?: boolean;
  renderDay?: (
    day: any,
    selectedDate: any,
    dayComponent: JSX.Element,
  ) => JSX.Element;
}

const Component = memo((props: Props) => {
  const now = new Date();

  const [selectedDate, setSelectedDate] = useState(props.value);

  const selectableBackgroundColor: string | undefined = useMemo(() => {
    return props.color ? convertHexToRGBA(props.color, 15) : undefined;
  }, [props.color]);

  CustomLuxonUtils.startWeekDay = props.startWeekDay || 0;

  const onDaySelect = (day: unknown) => {
    const selected = day as DateTime;
    setSelectedDate(selected);
    props.onSelect(selected);
  };

  const renderDay = (
    date: DateTime | any,
    selectedDates: Array<DateTime | any | null>,
    pickersDayProps: PickersDayProps<DateTime | any>,
  ) => {
    const renderDate = date as DateTime;
    const selected =
      renderDate.year === selectedDate?.year &&
      renderDate.month === selectedDate?.month &&
      renderDate.day === selectedDate?.day;

    const el = (
      <StyledPickersDay
        {...pickersDayProps}
        selected={selected}
        onDaySelect={onDaySelect}
        selectedBackgroundColor={props.color}
        selectableBackgroundColor={selectableBackgroundColor}
        isSelectedBackgroundColorLight={props.isLightColor}
        isLoading={props.isLoading}
        isAvailable={props.isAvailable}
      />
    );

    if (props.renderDay) {
      return props.renderDay(date, selectedDates[0], el);
    }

    return el;
  };

  return (
    <Container ref={props.datePickerRef}>
      <LocalizationProvider
        dateAdapter={CustomLuxonUtils}
        adapterLocale={props.locale}
      >
        <StaticDatePicker
          // autoOk
          // disabled={true}
          minDate={
            props.isSetMinDateUndefined ? undefined : props.minDate || now
          }
          maxDate={props.maxDate}
          renderInput={(params) => <TextField {...params} />}
          showToolbar={false}
          disablePast={props.disablePast}
          displayStaticWrapperAs={'desktop'}
          // initialFocusedDate={props.defaultDate}
          openTo="day"
          onMonthChange={props.onChangeMonth}
          value={props.value?.toISO()}
          onChange={() => {}}
          shouldDisableDate={props.shouldDisableDate}
          renderDay={renderDay}
          reduceAnimations
          views={['day']}
          loading={props.isLoading}
          renderLoading={() => <Skeleton view="DatePicker" />}
        />
      </LocalizationProvider>
    </Container>
  );
});

const StyledPickersDay = styled(PickersDay)<{
  selectedBackgroundColor?: string;
  selectableBackgroundColor?: string;
  isSelectedBackgroundColorLight?: boolean;
  isLoading?: boolean;
  isAvailable?: boolean;
}>`
  width: 40px !important;
  height: 40px !important;
  color: ${black} !important;
  margin: 2px 2px !important;
  font-size: ${fontSize(16)} !important;

  ${mediaMax.smartphone} {
    width: 36px !important;
    height: 36px !important;
  }

  ${(props) => css`
    background-color: ${props.selectableBackgroundColor || ''} !important;
  `}
  ${(props) =>
    props.disabled &&
    css`
      color: ${black(props)} !important;
      opacity: 0.3;
      background-color: ${white(props)} !important;
    `};

  ${(props) =>
    props.selected &&
    css`
      color: ${props.isSelectedBackgroundColorLight
        ? black(props)
        : white(props)} !important;
      background-color: ${props.selectedBackgroundColor || ''} !important;
    `};
  ${(props) =>
    props.today &&
    css`
      border: 0 !important;
    `};

  :hover {
    color: ${(props) =>
      props.isSelectedBackgroundColorLight ? black : white} !important;
    background-color: ${(props) =>
      props.selectedBackgroundColor || ''} !important;
  }

  &.MuiPickersDay-hiddenDaySpacingFiller {
    background-color: transparent !important;
    &:hover {
      background-color: transparent !important;
    }
  }
`;

const Container = styled.div`
  div[role='row'] {
    span {
      width: 40px;
      color: ${black};
      font-weight: ${fontWeight('bold')};
      ${mediaMax.smartphone} {
        width: 36px;
      }
    }
  }
`;

export default Component;
